import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { computeDraw } from "@/lib/calc";
import { Zap, Loader2, MessageCircle, Sparkles } from "lucide-react";
import SyringeViz from "@/components/SyringeViz";
import CoachPanel from "@/components/CoachPanel";
import DmModal from "@/components/DmModal";
import InjectionSites from "@/components/InjectionSites";
import HGHCalculator from "@/components/HGHCalculator";
import TRTCalculator from "@/components/TRTCalculator";

const SYRINGES = [
  { id: "u100-1ml", label: "U-100 · 1 mL (100 units)", units: 100, ml: 1.0 },
  { id: "u100-0.5ml", label: "U-100 · 0.5 mL (50 units)", units: 50, ml: 0.5 },
  { id: "u100-0.3ml", label: "U-100 · 0.3 mL (30 units)", units: 30, ml: 0.3 },
  { id: "u40-1ml", label: "U-40 · 1 mL (40 units)", units: 40, ml: 1.0 },
];

const BAC_PRESETS = [1, 2, 3, 4, 5];

export default function PeptideCalculator() {
  const [peptides, setPeptides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dmOpen, setDmOpen] = useState(false);

  const [peptideSlug, setPeptideSlug] = useState("");
  const [vialValue, setVialValue] = useState(5);
  const [vialUnit, setVialUnit] = useState("mg");
  const [bacMl, setBacMl] = useState(2);
  const [doseValue, setDoseValue] = useState(250);
  const [doseUnit, setDoseUnit] = useState("mcg");
  const [syringeId, setSyringeId] = useState(SYRINGES[0].id);
  const [sex, setSex] = useState(() => localStorage.getItem("pepiq_sex") || "male");
  const [customMix, setCustomMix] = useState([]); // [{ slug, name, mg }]
  const [shotsPerVial, setShotsPerVial] = useState(30);
  const [tab, setTab] = useState(() => localStorage.getItem("pepiq_tab") || "peptide"); // "peptide" | "hgh"
  const [savedCalcs, setSavedCalcs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pepiq_saved") || "[]"); } catch { return []; }
  });
  const [showReverse, setShowReverse] = useState(false);

  useEffect(() => {
    api.get("/peptides").then((r) => setPeptides(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { localStorage.setItem("pepiq_sex", sex); }, [sex]);
  useEffect(() => { localStorage.setItem("pepiq_tab", tab); }, [tab]);

  const syringe = useMemo(() => SYRINGES.find((s) => s.id === syringeId) || SYRINGES[0], [syringeId]);
  const selectedPeptide = useMemo(() => peptides.find((p) => p.slug === peptideSlug), [peptides, peptideSlug]);
  const isStack = selectedPeptide?.type === "stack" || peptideSlug === "__custom_blend__";

  // Total premix mg (sum of all component mg in the manual mix)
  const customMixTotalMg = useMemo(
    () => customMix.reduce((sum, c) => sum + (parseFloat(c.mg) || 0), 0),
    [customMix]
  );

  // Group peptides for dropdown — stacks first, then by category (memoized so JSX render is cheap)
  const grouped = useMemo(() => {
    const stacks = peptides.filter((p) => p.type === "stack");
    const regular = peptides.filter((p) => p.type !== "stack");
    const byCat = {};
    for (const p of regular) {
      const c = p.category || "Other";
      (byCat[c] ||= []).push(p);
    }
    const sortedCats = Object.entries(byCat).sort(([a], [b]) => a.localeCompare(b));
    return { stacks, sortedCats };
  }, [peptides]);

  function applyDoseForSex(p, sexKey) {
    // Returns default doseValue + doseUnit for a given peptide based on sex
    if (p.vial_unit === "iu" && p.vial_size) {
      const iu = sexKey === "female" ? (p.dose_female_iu ?? p.typical_dose_mcg ?? 1) : (p.dose_male_iu ?? p.typical_dose_mcg ?? 2);
      return { value: iu, unit: "iu" };
    }
    const mcg = sexKey === "female" ? (p.dose_female_mcg ?? p.typical_dose_mcg) : (p.dose_male_mcg ?? p.typical_dose_mcg);
    return { value: mcg, unit: "mcg" };
  }

  function pickPeptide(slug) {
    setPeptideSlug(slug);
    const p = peptides.find((x) => x.slug === slug);
    if (!p) return;
    if (p.type === "stack") {
      applyStackDefaults(p, sex);
      return;
    }
    applyPeptideDefaults(p, sex);
  }

  function applyStackDefaults(stack, sexKey) {
    // Seed customMix from the first premix's components (sensible starting values)
    const seed = stack.premixes?.[0];
    const components = seed?.components?.map((c) => ({ slug: c.slug, name: c.name, mg: c.mg })) || [];
    setCustomMix(components);
    const shots = sexKey === "female"
      ? (seed?.shots_female ?? seed?.shots_male ?? 30)
      : (seed?.shots_male ?? seed?.shots_female ?? 30);
    setShotsPerVial(shots);
    const totalMg = components.reduce((sum, c) => sum + (parseFloat(c.mg) || 0), 0);
    setVialValue(totalMg);
    setVialUnit("mg");
    setDoseValue(Math.round((totalMg * 1000) / shots));
    setDoseUnit("mcg");
  }

  function updateComponentMg(slug, mg) {
    setCustomMix((prev) => {
      const next = prev.map((c) => (c.slug === slug ? { ...c, mg } : c));
      const totalMg = next.reduce((sum, c) => sum + (parseFloat(c.mg) || 0), 0);
      setVialValue(totalMg);
      const shots = parseFloat(shotsPerVial) || 1;
      setDoseValue(Math.round((totalMg * 1000) / shots));
      setDoseUnit("mcg");
      return next;
    });
  }

  function changeShots(n) {
    setShotsPerVial(n);
    if (isStack) {
      const shots = parseFloat(n) || 1;
      setDoseValue(Math.round((customMixTotalMg * 1000) / shots));
      setDoseUnit("mcg");
    }
  }

  function applyPeptideDefaults(p, sexKey) {
    // Vial: IU-dosed peptides (HCG/HGH) use vial_unit=iu + vial_size; others mg + vial_mg
    if (p.vial_unit === "iu" && p.vial_size) {
      setVialValue(p.vial_size);
      setVialUnit("iu");
    } else if (p.vial_mg && p.vial_mg > 0) {
      setVialValue(p.vial_mg);
      setVialUnit("mg");
    }
    const d = applyDoseForSex(p, sexKey);
    if (d.value != null) {
      setDoseValue(d.value);
      setDoseUnit(d.unit);
    }
  }

  function handleSexChange(next) {
    setSex(next);
    if (selectedPeptide && selectedPeptide.type !== "stack") {
      const d = applyDoseForSex(selectedPeptide, next);
      if (d.value != null) {
        setDoseValue(d.value);
        setDoseUnit(d.unit);
      }
    } else if (selectedPeptide && selectedPeptide.type === "stack") {
      // Only re-seed shots count for sex; keep user's mg edits
      const seed = selectedPeptide.premixes?.[0];
      const shots = next === "female"
        ? (seed?.shots_female ?? seed?.shots_male ?? 30)
        : (seed?.shots_male ?? seed?.shots_female ?? 30);
      setShotsPerVial(shots);
      setDoseValue(Math.round((customMixTotalMg * 1000) / shots));
      setDoseUnit("mcg");
    }
  }

  // ---------- Custom Blend (user-defined stack) ----------
  function startCustomBlend() {
    setPeptideSlug("__custom_blend__");
    setCustomMix([]);
    setShotsPerVial(sex === "female" ? 33 : 25);
    setVialValue(0);
    setVialUnit("mg");
    setDoseValue(0);
    setDoseUnit("mcg");
  }

  function addBlendPeptide(slug) {
    const p = peptides.find((x) => x.slug === slug);
    if (!p || p.type === "stack") return;
    const defaultMg = p.vial_unit === "iu" ? 5 : (p.vial_mg || 5);
    setCustomMix((prev) => {
      if (prev.some((c) => c.slug === slug)) return prev;
      if (prev.length >= 3) return prev; // reference site limit
      const next = [...prev, { slug, name: p.name, mg: defaultMg }];
      const totalMg = next.reduce((sum, c) => sum + (parseFloat(c.mg) || 0), 0);
      setVialValue(totalMg);
      setDoseValue(Math.round((totalMg * 1000) / (parseFloat(shotsPerVial) || 1)));
      setDoseUnit("mcg");
      return next;
    });
  }

  function removeBlendPeptide(slug) {
    setCustomMix((prev) => {
      const next = prev.filter((c) => c.slug !== slug);
      const totalMg = next.reduce((sum, c) => sum + (parseFloat(c.mg) || 0), 0);
      setVialValue(totalMg);
      setDoseValue(Math.round((totalMg * 1000) / (parseFloat(shotsPerVial) || 1)));
      setDoseUnit("mcg");
      return next;
    });
  }

  const isCustomBlend = peptideSlug === "__custom_blend__";

  // ---------- Save / Load ----------
  function saveCalculation() {
    const title = window.prompt("Name this calculation (e.g., 'BPC-157 daily'):", selectedPeptide?.name || (isCustomBlend ? "Custom blend" : "My calc"));
    if (!title) return;
    const snap = {
      id: `s_${Date.now()}`,
      title,
      peptideSlug,
      customMix: isCustomBlend || isStack ? customMix : [],
      vialValue, vialUnit, bacMl, doseValue, doseUnit, syringeId, shotsPerVial, sex,
      savedAt: new Date().toISOString(),
    };
    const next = [snap, ...savedCalcs].slice(0, 12); // cap at 12
    setSavedCalcs(next);
    localStorage.setItem("pepiq_saved", JSON.stringify(next));
  }

  function loadSavedCalc(id) {
    const snap = savedCalcs.find((s) => s.id === id);
    if (!snap) return;
    setPeptideSlug(snap.peptideSlug || "");
    if (snap.customMix && snap.customMix.length) setCustomMix(snap.customMix);
    setVialValue(snap.vialValue);
    setVialUnit(snap.vialUnit);
    setBacMl(snap.bacMl);
    setDoseValue(snap.doseValue);
    setDoseUnit(snap.doseUnit);
    setSyringeId(snap.syringeId);
    setShotsPerVial(snap.shotsPerVial ?? 30);
    if (snap.sex) setSex(snap.sex);
  }

  function deleteSavedCalc(id) {
    const next = savedCalcs.filter((s) => s.id !== id);
    setSavedCalcs(next);
    localStorage.setItem("pepiq_saved", JSON.stringify(next));
  }

  const result = useMemo(
    () => computeDraw({ vialValue, vialUnit, bacMl, doseValue, doseUnit, syringe }),
    [vialValue, vialUnit, bacMl, doseValue, doseUnit, syringe]
  );

  // Map the selected peptide / stack to an injection route the body map can highlight.
  const recommendedRoute = useMemo(() => {
    if (!selectedPeptide) return null;
    // Stacks: take the dominant route — almost all popular stacks (Glow/KLOW/Wolverine) inject SubQ.
    if (isStack) {
      const freqs = (selectedPeptide.components || [])
        .map((c) => (c.freq || "").toLowerCase())
        .join(" ");
      if (freqs.includes("subq") || freqs.includes("subcut")) return "SubQ";
      if (freqs.includes(" im") || freqs.includes("intramuscular")) return "IM";
      return "SubQ"; // sensible default
    }
    const r = (selectedPeptide.route || "").toLowerCase();
    // Order matters — "subcutaneous / intramuscular" → SubQ (primary). Pure "intramuscular" → IM.
    if (r.includes("subcut")) return "SubQ";
    if (r.includes("intramuscular") || r === "im") return "IM";
    return null;
  }, [selectedPeptide, isStack]);

  const aiContext = selectedPeptide
    ? `User is calculating a dose for ${selectedPeptide.name} (${selectedPeptide.category}). Vial: ${vialValue}${vialUnit}, BAC water: ${bacMl}mL, target dose: ${doseValue}${doseUnit}. Calculator shows draw to ${result && !result.mismatch ? result.unitsNeeded?.toFixed(1) : "?"} units on a U-${syringe.units} syringe.`
    : "";

  return (
    <div data-testid="calculator-page" className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-[#262626]">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-3">
          <span className="w-8 h-8 grid place-items-center bg-[#CCFF00] rounded text-black">
            <Zap size={16} strokeWidth={3} />
          </span>
          <span className="font-heading font-black tracking-tighter text-xl">Pep<span className="text-[#CCFF00]">IQ</span></span>
          <span className="ml-auto text-xs font-mono-data text-[#A3A3A3] hidden sm:inline">Reconstitution calculator</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {/* Calculator mode tabs */}
        <div data-testid="calc-tabs" className="inline-flex flex-wrap bg-[#0A0A0A] border border-[#262626] rounded-lg p-1 mb-8 gap-1" role="tablist" aria-label="Calculator mode">
          <button
            type="button"
            data-testid="tab-peptide"
            onClick={() => setTab("peptide")}
            aria-pressed={tab === "peptide"}
            className={`px-5 py-2.5 rounded font-mono-data text-xs font-bold tracking-wider uppercase transition-colors ${
              tab === "peptide" ? "bg-[#CCFF00] text-black" : "text-[#A3A3A3] hover:text-white"
            }`}
          >
            Peptide Calculator
          </button>
          <button
            type="button"
            data-testid="tab-hgh"
            onClick={() => setTab("hgh")}
            aria-pressed={tab === "hgh"}
            className={`px-5 py-2.5 rounded font-mono-data text-xs font-bold tracking-wider uppercase transition-colors ${
              tab === "hgh" ? "bg-[#00E5FF] text-black" : "text-[#A3A3A3] hover:text-white"
            }`}
          >
            HGH Calculator
          </button>
          <button
            type="button"
            data-testid="tab-trt"
            onClick={() => setTab("trt")}
            aria-pressed={tab === "trt"}
            className={`px-5 py-2.5 rounded font-mono-data text-xs font-bold tracking-wider uppercase transition-colors ${
              tab === "trt" ? "bg-[#FF8A3D] text-black" : "text-[#A3A3A3] hover:text-white"
            }`}
          >
            TRT Calculator
          </button>
        </div>

        {tab === "hgh" ? (
          <HGHCalculator />
        ) : tab === "trt" ? (
          <TRTCalculator />
        ) : (
        <>
        <div className="mb-8">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-3">/ DOSE CALCULATOR</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black">How much do I draw?</h1>
          <p className="text-[#A3A3A3] mt-3 max-w-xl">Pick your peptide, vial size, BAC water and target dose. We&apos;ll show you exactly where to fill the syringe.</p>
        </div>

        {/* Action bar — save + reverse calc + saved calcs */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="calc-actions">
          <button
            type="button"
            data-testid="calc-save"
            onClick={saveCalculation}
            className="px-4 py-2 rounded-md font-mono-data text-xs font-bold uppercase tracking-wider bg-[#CCFF00] text-black hover:bg-[#B3E600] transition-colors"
          >
            ★ Save this calc
          </button>
          <button
            type="button"
            data-testid="calc-reverse-toggle"
            onClick={() => setShowReverse((v) => !v)}
            aria-pressed={showReverse}
            className={`px-4 py-2 rounded-md font-mono-data text-xs font-bold uppercase tracking-wider transition-colors border ${
              showReverse ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#121212] text-[#A3A3A3] border-[#262626] hover:text-white"
            }`}
          >
            ↺ Reverse calc {showReverse ? "▲" : "▼"}
          </button>
          {savedCalcs.length > 0 && (
            <span className="text-[10px] text-[#A3A3A3] font-mono-data ml-1">{savedCalcs.length} saved · pick below to reload</span>
          )}
        </div>

        {showReverse && <ReverseCalcCard />}

        {savedCalcs.length > 0 && (
          <SavedCalcsPanel saved={savedCalcs} onLoad={loadSavedCalc} onDelete={deleteSavedCalc} />
        )}

        {/* Syringe visualization */}
        <div data-testid="syringe-card" className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6 sm:p-10 mb-8">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#A3A3A3]" /></div>
          ) : (
            <SyringeViz units={result && !result.mismatch ? result.unitsNeeded || 0 : 0} maxUnits={syringe.units} />
          )}

          {result && !result.mismatch && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#262626] border border-[#262626] rounded-lg overflow-hidden">
              <Stat k="Draw to" v={`${result.unitsNeeded.toFixed(1)} u`} highlight />
              <Stat k="Volume" v={`${result.mlNeeded.toFixed(3)} mL`} />
              <Stat k={isStack ? "Total / shot" : "Concentration"} v={isStack ? `${doseValue} mcg` : `${Math.round(result.concentrationPerMl)} ${doseUnit}/mL`} />
              <Stat k={isStack ? "Shots/vial" : "Doses/vial"} v={isStack ? `${shotsPerVial}` : result.dosesPerVial.toFixed(1)} />
            </div>
          )}

          {isStack && customMix.length > 0 && result && !result.mismatch && (
            <PremixBreakdown components={customMix} shots={parseFloat(shotsPerVial) || 1} units={result.unitsNeeded} />
          )}

          {result?.mismatch && (
            <div data-testid="mismatch-warning" className="mt-4 bg-[#FF3366]/10 border border-[#FF3366]/40 rounded p-4 text-sm text-[#FF3366]">
              ⚠ Can&apos;t safely convert <strong>{vialUnit}</strong> vial → <strong>{doseUnit}</strong> dose. IU↔mg conversions are peptide-specific. Set both vial and dose to the same unit (or use the peptide dropdown above to auto-set the right units).
            </div>
          )}

          {result?.overflow && !result?.mismatch && (
            <div data-testid="overflow-warning" className="mt-4 bg-[#FF3366]/10 border border-[#FF3366]/40 rounded p-4 text-sm text-[#FF3366]">
              ⚠ This dose exceeds the syringe capacity ({syringe.units} units). Use more BAC water, a larger syringe, or split into multiple draws.
            </div>
          )}
        </div>

        {/* Form */}
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6 sm:p-8 space-y-6 mb-12">
          <Field label="Peptide" hint="Pick a listed peptide, choose a pre-mixed stack, or build your own custom blend of up to 3 peptides.">
            <select
              data-testid="calc-peptide"
              value={peptideSlug}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__custom_blend__") { startCustomBlend(); return; }
                pickPeptide(v);
              }}
              className={`${inp} w-full`}
            >
              <option value="">— Custom / not listed —</option>
              <option value="__custom_blend__">✚ Build custom blend (up to 3 peptides)</option>
              {grouped.stacks.length > 0 && (
                <optgroup label="★ POPULAR STACKS">
                  {grouped.stacks.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.name}</option>
                  ))}
                </optgroup>
              )}
              {grouped.sortedCats.map(([cat, list]) => (
                <optgroup key={cat} label={cat}>
                  {list.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          {/* Sex toggle — affects auto-filled dose recommendations */}
          <Field label="Recommended dose for" hint="Adjusts the auto-filled dose based on common community guidance.">
            <div className="inline-flex bg-[#121212] border border-[#262626] rounded-md p-1" role="tablist" aria-label="Sex preference">
              {[
                { id: "male", label: "Male" },
                { id: "female", label: "Female" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`sex-${opt.id}`}
                  onClick={() => handleSexChange(opt.id)}
                  aria-pressed={sex === opt.id}
                  className={`px-5 py-2 rounded font-mono-data text-xs font-bold tracking-wider uppercase transition-colors ${
                    sex === opt.id
                      ? "bg-[#CCFF00] text-black"
                      : "text-[#A3A3A3] hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>

          {selectedPeptide && !isStack && (
            <div className="text-xs font-mono-data text-[#A3A3A3] bg-[#121212] rounded p-3 border border-[#262626]">
              <span className="text-[#CCFF00]">›</span> {selectedPeptide.tagline} — typical {doseValue}{doseUnit === "iu" ? " IU" : ` ${doseUnit}`} ({sex}), {selectedPeptide.frequency}
            </div>
          )}

          {isStack && (
            <StackMixEditor
              stack={isCustomBlend ? { name: "Custom Blend", tagline: "Combine up to 3 peptides in a single vial.", duration_weeks: null } : selectedPeptide}
              mix={customMix}
              totalMg={customMixTotalMg}
              onChange={updateComponentMg}
              customBlend={isCustomBlend}
              availablePeptides={grouped.sortedCats.flatMap(([, list]) => list)}
              onAddPeptide={addBlendPeptide}
              onRemovePeptide={removeBlendPeptide}
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label={isStack ? "Premix vial total" : "Vial size"} hint={isStack ? "Auto-set from premix selection. Edit if your vial is different." : undefined}>
              <div className="flex gap-2">
                <input
                  data-testid="calc-vial"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  value={vialValue}
                  onChange={(e) => setVialValue(e.target.value)}
                  className={`${inp} flex-1 min-w-0`}
                />
                <select
                  data-testid="calc-vial-unit"
                  value={vialUnit}
                  onChange={(e) => setVialUnit(e.target.value)}
                  className={`${inp} w-24 shrink-0`}
                >
                  <option value="mg">mg</option>
                  <option value="mcg">mcg</option>
                  <option value="iu">IU</option>
                </select>
              </div>
            </Field>

            <Field label="BAC water (mL)" hint="How much bacteriostatic water you'll add">
              <div className="flex gap-2">
                <input
                  data-testid="calc-bac"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  value={bacMl}
                  onChange={(e) => setBacMl(e.target.value)}
                  className={`${inp} flex-1 min-w-0`}
                />
                <div className="flex gap-1">
                  {BAC_PRESETS.map((b) => (
                    <button
                      key={b}
                      type="button"
                      data-testid={`bac-preset-${b}`}
                      onClick={() => setBacMl(b)}
                      className={`px-3 rounded font-mono-data text-xs font-bold border transition-colors ${
                        parseFloat(bacMl) === b
                          ? "bg-[#CCFF00] border-[#CCFF00] text-black"
                          : "bg-[#121212] border-[#262626] text-[#A3A3A3] hover:text-white"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
            </Field>

            {isStack ? (
              <Field label="Shots per vial" hint="How many daily shots you'll get from this vial. Sex preset applied — adjust to your protocol.">
                <input
                  data-testid="calc-shots"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={shotsPerVial}
                  onChange={(e) => changeShots(e.target.value)}
                  className={`${inp} w-full`}
                />
              </Field>
            ) : (
              <Field label="Desired dose">
                <div className="flex gap-2">
                  <input
                    data-testid="calc-dose-value"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    value={doseValue}
                    onChange={(e) => setDoseValue(e.target.value)}
                    className={`${inp} flex-1 min-w-0`}
                  />
                  <select
                    data-testid="calc-dose-unit"
                    value={doseUnit}
                    onChange={(e) => setDoseUnit(e.target.value)}
                    className={`${inp} w-24 shrink-0`}
                  >
                    <option value="mcg">mcg</option>
                    <option value="mg">mg</option>
                    <option value="iu">IU</option>
                  </select>
                </div>
              </Field>
            )}

            <Field label="Syringe" hint="Insulin syringe volume">
              <div className="grid grid-cols-3 gap-2">
                {SYRINGES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`calc-syringe-${s.id}`}
                    onClick={() => setSyringeId(s.id)}
                    aria-pressed={syringeId === s.id}
                    className={`py-3 rounded-md font-mono-data text-xs font-bold uppercase tracking-wider transition-colors ${
                      syringeId === s.id
                        ? "bg-[#CCFF00] text-black border border-[#CCFF00]"
                        : "bg-[#121212] text-[#A3A3A3] border border-[#262626] hover:text-white"
                    }`}
                  >
                    {s.units}u
                    <span className="block text-[9px] opacity-70 mt-0.5">{s.ml} mL</span>
                  </button>
                ))}
              </div>
            </Field>
          </div>
          {isStack && selectedPeptide?.components && (
            <StackReferenceCard stack={selectedPeptide} sex={sex} onLoad={(slug) => pickPeptide(slug)} />
          )}
        </div>
        </>
        )}

        {/* Injection sites */}
        <section data-testid="inject-section" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00]">/ WHERE TO INJECT</div>
            <div className="h-px flex-1 bg-[#262626]" />
          </div>
          <InjectionSites recommendedRoute={recommendedRoute} peptideName={selectedPeptide?.name} />
        </section>

        {/* AI Coach Section */}
        <section data-testid="coach-section" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] flex items-center gap-2">
              <Sparkles size={14} /> / AI COACH
            </div>
            <div className="h-px flex-1 bg-[#262626]" />
          </div>
          <CoachPanel context={aiContext} />
        </section>

        {/* DM Section */}
        <section data-testid="dm-section" className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] flex items-center gap-2">
              <MessageCircle size={14} /> / DM US
            </div>
            <div className="h-px flex-1 bg-[#262626]" />
          </div>
          <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6 sm:p-8 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-heading text-xl font-bold mb-2">Need a custom protocol?</h3>
              <p className="text-sm text-[#A3A3A3]">Quick DM on WhatsApp, SMS, or send an inquiry — we&apos;ll help you dial in your dose, sourcing, or stack.</p>
            </div>
            <button
              data-testid="dm-open-btn"
              onClick={() => setDmOpen(true)}
              className="inline-flex items-center gap-2 bg-[#CCFF00] text-black font-bold px-6 py-3 rounded-md hover:bg-[#B3E600] transition-colors"
            >
              <MessageCircle size={16} /> Open DM options
            </button>
          </div>
        </section>

        <p className="text-xs text-[#A3A3A3] font-mono-data mt-10 text-center">
          Always verify math with a second source. Educational only — not medical advice.
        </p>
      </main>

      <DmModal
        open={dmOpen}
        onClose={() => setDmOpen(false)}
        peptide={selectedPeptide || null}
      />
    </div>
  );
}

const inp =
  "bg-[#121212] border border-[#262626] text-white rounded-md px-4 py-3 focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] transition-all font-mono-data text-sm";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-[#A3A3A3] mb-2 block">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-[#A3A3A3] mt-2 font-mono-data">{hint}</p>}
    </div>
  );
}

function Stat({ k, v, highlight }) {
  return (
    <div className={`p-4 ${highlight ? "bg-[#CCFF00]" : "bg-[#0A0A0A]"}`}>
      <div className={`text-[10px] tracking-widest uppercase ${highlight ? "text-black/70" : "text-[#A3A3A3]"}`}>{k}</div>
      <div className={`font-mono-data font-bold text-xl mt-1 ${highlight ? "text-black" : "text-white"}`}>{v}</div>
    </div>
  );
}

function StackMixEditor({ stack, mix, totalMg, onChange, customBlend = false, availablePeptides = [], onAddPeptide = null, onRemovePeptide = null }) {
  if (!stack) return null;
  if (!customBlend && (!Array.isArray(mix) || mix.length === 0)) return null;
  const usedSlugs = new Set(mix.map((c) => c.slug));
  const addable = availablePeptides.filter((p) => !usedSlugs.has(p.slug));
  const atLimit = mix.length >= 3;
  return (
    <div data-testid="mix-editor" className="bg-[#121212] border border-[#FFD700]/30 rounded-lg p-5">
      <div className="flex flex-wrap items-baseline gap-3 mb-3">
        <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FFD700]">{customBlend ? "✚ Custom Blend" : "★ Manual Premix"}</span>
        <span className="text-xs text-[#A3A3A3] font-mono-data">{stack.duration_weeks ? `${stack.duration_weeks} wks on / ${stack.off_weeks || 0} off` : ""}</span>
      </div>
      <h3 className="font-heading text-xl font-black mb-1">{stack.name}</h3>
      <p className="text-xs text-[#A3A3A3] mb-4">{stack.tagline}</p>

      <label className="text-sm font-semibold text-[#A3A3A3] mb-2 block">
        {customBlend ? `Peptides in your vial (${mix.length}/3)` : "Enter your vial's mg per component"}
      </label>
      {!customBlend && (
        <p className="text-[10px] text-[#A3A3A3] mb-3 font-mono-data">Vendors blend stacks at different ratios — type the exact mg printed on your vial label.</p>
      )}

      <div className="space-y-2 mb-4">
        {mix.map((c) => (
          <div key={c.slug} data-testid={`mix-row-${c.slug}`} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded-md px-3 py-2">
            <span className="text-sm font-bold text-white flex-1 min-w-0">{c.name}</span>
            <input
              data-testid={`mix-input-${c.slug}`}
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              value={c.mg}
              onChange={(e) => onChange(c.slug, e.target.value)}
              className="w-24 bg-[#121212] border border-[#262626] text-white rounded px-3 py-2 focus:outline-none focus:border-[#FFD700] focus:ring-1 focus:ring-[#FFD700] transition-all font-mono-data text-sm text-right"
            />
            <span className="text-xs text-[#A3A3A3] font-mono-data w-8">mg</span>
            {customBlend && onRemovePeptide && (
              <button
                type="button"
                data-testid={`mix-remove-${c.slug}`}
                onClick={() => onRemovePeptide(c.slug)}
                className="text-[10px] font-bold uppercase text-[#FF3366] hover:text-white transition-colors px-2"
                aria-label={`Remove ${c.name}`}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {customBlend && onAddPeptide && (
        <div className="mb-4">
          <select
            data-testid="blend-add-peptide"
            value=""
            disabled={atLimit || addable.length === 0}
            onChange={(e) => { if (e.target.value) onAddPeptide(e.target.value); }}
            className="w-full bg-[#0A0A0A] border border-[#FFD700]/40 text-white rounded-md px-4 py-3 focus:outline-none focus:border-[#FFD700] focus:ring-1 focus:ring-[#FFD700] transition-all font-mono-data text-sm disabled:opacity-50"
          >
            <option value="">{atLimit ? "★ Max 3 peptides — remove one to add another" : "✚ Add a peptide to this blend…"}</option>
            {addable.map((p) => (
              <option key={p.slug} value={p.slug}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      <div data-testid="mix-total" className="flex items-center justify-between bg-[#0A0A0A] border border-[#FFD700]/30 rounded-md px-4 py-3">
        <span className="text-xs font-bold tracking-widest uppercase text-[#FFD700]">Total {customBlend ? "blend" : "premix"} vial</span>
        <span className="font-mono-data font-bold text-lg text-[#FFD700]">{totalMg.toFixed(totalMg < 10 ? 1 : 0)} mg</span>
      </div>
    </div>
  );
}

function PremixBreakdown({ components, shots, units }) {
  if (!Array.isArray(components) || components.length === 0 || !shots) return null;
  // Each component's mcg per shot = (component_mg * 1000) / shots_per_vial
  return (
    <div data-testid="premix-breakdown" className="mt-6 bg-[#121212] border border-[#FFD700]/20 rounded-lg p-5">
      <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FFD700] mb-3">
        ★ Per shot ({units.toFixed(1)} u) you deliver
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {components.map((c) => {
          const mgVal = parseFloat(c.mg) || 0;
          const mcgPerShot = (mgVal * 1000) / shots;
          return (
            <div key={c.slug} data-testid={`breakdown-${c.slug}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
              <div className="text-xs text-[#A3A3A3] font-mono-data">{c.name}</div>
              <div className="font-mono-data font-bold text-lg text-[#FFD700] mt-1">
                {mcgPerShot >= 1000 ? `${(mcgPerShot / 1000).toFixed(2)} mg` : `${Math.round(mcgPerShot)} mcg`}
              </div>
              <div className="text-[10px] text-[#A3A3A3] mt-0.5">{mgVal} mg in vial</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StackReferenceCard({ stack, sex, onLoad }) {
  if (!stack || !Array.isArray(stack.components)) return null;
  return (
    <div data-testid="stack-reference" className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4 mt-5">
      <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A3A3A3] mb-3">
        Reference · standalone protocol dosing
      </div>
      <div className="space-y-2">
        {stack.components.map((c) => {
          const dose = sex === "female" ? (c.dose_female || c.dose_male) : (c.dose_male || c.dose_female);
          return (
            <div key={c.slug} data-testid={`stack-component-${c.slug}`} className="flex flex-wrap items-center gap-3 text-xs font-mono-data">
              <span className="text-white font-bold min-w-[110px]">{c.name}</span>
              <span className="text-[#CCFF00]">{dose}</span>
              <span className="text-[#A3A3A3]">· {c.freq}</span>
              <button
                type="button"
                data-testid={`stack-load-${c.slug}`}
                onClick={() => onLoad(c.slug)}
                className="ml-auto text-[10px] font-bold tracking-wider uppercase text-[#CCFF00] hover:text-white transition-colors"
              >
                Calc standalone →
              </button>
            </div>
          );
        })}
      </div>
      {stack.what_to_expect && (
        <p className="text-[10px] font-mono-data text-[#A3A3A3] mt-4 pt-3 border-t border-[#262626]">
          <span className="text-[#FFD700]">›</span> {stack.what_to_expect}
        </p>
      )}
    </div>
  );
}


function SavedCalcsPanel({ saved, onLoad, onDelete }) {
  return (
    <div data-testid="saved-calcs" className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4 mb-6">
      <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A3A3A3] mb-3">★ Saved calculations</div>
      <div className="flex flex-wrap gap-2">
        {saved.map((s) => (
          <div key={s.id} data-testid={`saved-${s.id}`} className="flex items-center gap-1 bg-[#121212] border border-[#262626] rounded-md px-3 py-2 hover:border-[#CCFF00]/40 transition-colors">
            <button
              type="button"
              data-testid={`saved-load-${s.id}`}
              onClick={() => onLoad(s.id)}
              className="font-mono-data text-xs text-white hover:text-[#CCFF00] transition-colors text-left"
              title={new Date(s.savedAt).toLocaleString()}
            >
              {s.title}
            </button>
            <button
              type="button"
              data-testid={`saved-delete-${s.id}`}
              onClick={() => onDelete(s.id)}
              className="text-[10px] font-bold text-[#A3A3A3] hover:text-[#FF3366] transition-colors px-1"
              aria-label={`Delete ${s.title}`}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReverseCalcCard() {
  const [vialMg, setVialMg] = useState(5);
  const [doseMcg, setDoseMcg] = useState(250);
  const [drawUnits, setDrawUnits] = useState(10);

  // BAC needed so that drawing `drawUnits` units on a U-100 syringe delivers `doseMcg`.
  // conc = vial_mcg / BAC_ml.  ml_per_dose = doseMcg / conc = (doseMcg * BAC_ml) / vial_mcg.
  // units = ml_per_dose * 100. Solve for BAC_ml: BAC_ml = (units * vial_mcg) / (100 * doseMcg).
  const bacMl = useMemo(() => {
    const v = parseFloat(vialMg);
    const d = parseFloat(doseMcg);
    const u = parseFloat(drawUnits);
    if (!v || !d || !u) return null;
    const vialMcg = v * 1000;
    return (u * vialMcg) / (100 * d);
  }, [vialMg, doseMcg, drawUnits]);

  return (
    <div data-testid="reverse-calc" className="bg-[#0A0A0A] border border-[#CCFF00]/30 rounded-lg p-5 mb-6">
      <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#CCFF00] mb-2">↺ Reverse Calculator</div>
      <p className="text-xs text-[#A3A3A3] mb-4">Tell me your vial size, dose, and how many units you want to draw — I&apos;ll tell you how much BAC water to add.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="text-xs text-[#A3A3A3] mb-1 block">Vial (mg)</label>
          <input
            data-testid="reverse-vial"
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            value={vialMg}
            onChange={(e) => setVialMg(e.target.value)}
            className="w-full bg-[#121212] border border-[#262626] text-white rounded-md px-3 py-2 focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] font-mono-data text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-[#A3A3A3] mb-1 block">Dose (mcg)</label>
          <input
            data-testid="reverse-dose"
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            value={doseMcg}
            onChange={(e) => setDoseMcg(e.target.value)}
            className="w-full bg-[#121212] border border-[#262626] text-white rounded-md px-3 py-2 focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] font-mono-data text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-[#A3A3A3] mb-1 block">Draw to (units, U-100)</label>
          <input
            data-testid="reverse-units"
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            value={drawUnits}
            onChange={(e) => setDrawUnits(e.target.value)}
            className="w-full bg-[#121212] border border-[#262626] text-white rounded-md px-3 py-2 focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] font-mono-data text-sm"
          />
        </div>
      </div>
      <div data-testid="reverse-result" className="bg-[#121212] border border-[#CCFF00]/40 rounded-md px-4 py-3 flex items-center justify-between">
        <span className="text-xs font-bold tracking-widest uppercase text-[#CCFF00]">BAC water needed</span>
        <span className="font-mono-data font-bold text-lg text-[#CCFF00]">{bacMl != null ? `${bacMl.toFixed(2)} mL` : "—"}</span>
      </div>
    </div>
  );
}

